import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../utils/idbUtils";

const DB_NAME = "grpc_fertilizacion";
const DB_VERSION = 3;
const STORE_ANALISIS = "analisis";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/`GrpcAplicacionItem`: watermark por finca, no uno global). */
export type GrpcAnalisisItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** IndexedDB **nueva y separada** para los análisis de laboratorio que trae
 *  `FertilizacionGrpcService` (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web) — mismo patrón que `GrpcLoteDatabase`/`GrpcAplicacionDatabase`:
 *  delta real por `fincaId`. */
class GrpcFertilizacionDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_ANALISIS, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Watermark de la última sincronización exitosa de una finca. `0n` si
   *  todavía no sincronizó nunca (trae el listado completo). */
  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  /** Aplica un delta de una finca: `put` de los análisis vivos, `delete` de
   *  los marcados `eliminado`, y guarda el nuevo watermark de esa finca. */
  async putDeltaAsync<T extends GrpcAnalisisItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_ANALISIS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_ANALISIS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Todos los análisis guardados de una finca, vía el índice `fincaId`. */
  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_ANALISIS, "readonly")
      .objectStore(STORE_ANALISIS)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) un solo análisis sin tocar el resto — lo llaman
   *  los guardados puntuales (parse/asignar-lotes) para que los consumidores
   *  vean el cambio sin esperar al próximo delta. */
  async putOneAsync<T extends GrpcAnalisisItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_ANALISIS, "readwrite");
    tx.objectStore(STORE_ANALISIS).put(item);
    await txDone(tx);
  }

  /** Borra un análisis puntual (baja confirmada por el server). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_ANALISIS, "readwrite");
    tx.objectStore(STORE_ANALISIS).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de análisis de Fertilización por gRPC-Web (con delta
 *  por finca). */
export const grpcFertilizacionDatabase = new GrpcFertilizacionDatabase();
